"use client";

import { sendGTMEvent } from "@next/third-parties/google";
import { useSession } from "next-auth/react";
import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { jwtDecode } from "jwt-decode";
import { LocaleEnum } from "~/locales/resources";
import { checkUserIsEmployee } from "~/utils/generalUtils";
import { useHotjar } from "~/hooks/useHotjar";
import { usePostHog } from "~/hooks/usePostHog";
import { QUERY_PARAM_PURCHASE_ORDER, QUERY_PARAM_POFE, QUERY_PARAM_POFE_DETAILS_MODAL } from "~/constants/queryParams";

type JwtClaims = {
  contact_id?: string;
  account_id?: string;
  account_name?: string;
  finished_setup?: boolean;
};

type Props = {
  locale?: LocaleEnum;
};

const Tracking = ({ locale }: Props) => {
  const { data: session } = useSession();
  const user = session?.user as { id: string; name: string; email: string };
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { identifyUser: hotjarIdentify, stateChange, triggerEvent, isEnabled: isHotjarEnabled } = useHotjar();
  const { capturePageView, identifyUser: posthogIdentify, isEnabled: isPostHogEnabled } = usePostHog();
  const previousPoRef = useRef<string | null>(null);
  const previousPofeRef = useRef<string | null>(null);
  const previousPofeModalRef = useRef<boolean>(false);
  const previousPathRef = useRef<string | null>(null);

  // Identify user across all tools on session load
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID && user) {
      const { id, name, email } = user;
      const isUserEmployee = checkUserIsEmployee(email || "");
      sendGTMEvent({
        event: "E_onLayoutLoad",
        user_id: id || "",
        user_name: name,
        user_email: email,
        user_isEmployee: isUserEmployee,
        user_locale: locale,
        ...(isUserEmployee && { traffic_type: "internal" }),
      });
    }

    // Identify user in Hotjar
    if (isHotjarEnabled && user) {
      const { id, name, email } = user;
      const isUserEmployee = checkUserIsEmployee(email || "");
      hotjarIdentify(id || "", {
        email: email || "",
        name: name || "",
        locale: locale || "",
        isEmployee: isUserEmployee,
      });
    }

    // Identify user in PostHog with rich traits so every event is attributed fully
    if (user?.id) {
      const { id, name, email, access_token } = user as {
        id: string;
        name: string;
        email: string;
        access_token?: string;
      };
      const isUserEmployee = checkUserIsEmployee(email || "");

      let jwtClaims: JwtClaims = {};
      try {
        if (access_token) jwtClaims = jwtDecode<JwtClaims>(access_token);
      } catch {
        // ignore malformed token
      }

      posthogIdentify(id, {
        // Standard PostHog person properties
        email: email || "",
        name: name || "",
        // App-specific traits
        locale: locale || "",
        user_type: isUserEmployee ? "employee" : "supplier",
        is_employee: isUserEmployee,
        // Salesforce identifiers
        salesforce_contact_id: jwtClaims.contact_id || "",
        salesforce_account_id: jwtClaims.account_id || "",
        // Account / company
        company_name: jwtClaims.account_name || "",
        // Onboarding state
        finished_setup: jwtClaims.finished_setup ?? null,
      });
    }
  }, [user, locale, hotjarIdentify, isHotjarEnabled, posthogIdentify]);

  // Track SPA route changes for all tools
  useEffect(() => {
    const currentPo = searchParams.get(QUERY_PARAM_PURCHASE_ORDER);
    const currentPofe = searchParams.get(QUERY_PARAM_POFE);
    const currentPofeModal = searchParams.get(QUERY_PARAM_POFE_DETAILS_MODAL) === "true";

    const fullPath =
      pathname +
      (currentPo ? `?po=${currentPo}` : "") +
      (currentPofe ? `${currentPo ? "&" : "?"}pofe=${currentPofe}` : "") +
      (currentPofeModal ? `${currentPo || currentPofe ? "&" : "?"}pofe-details-modal=true` : "");

    // PostHog: capture page view on every path change.
    // Only mark the path as "seen" when PostHog is actually ready to fire the event.
    // If PostHog is still initialising on first mount, we skip updating the ref so that
    // the event retries once PostHog becomes available (isPostHogEnabled changes → effect re-runs).
    if (fullPath !== previousPathRef.current) {
      if (isPostHogEnabled) {
        capturePageView(typeof window !== "undefined" ? window.location.href : fullPath);
        previousPathRef.current = fullPath;
      }
    }

    // Hotjar state + event tracking
    if (isHotjarEnabled) {
      stateChange(fullPath);

      if (currentPo && currentPo !== previousPoRef.current) {
        triggerEvent("po_changed");
        previousPoRef.current = currentPo;
      }

      if (currentPofe && currentPofe !== previousPofeRef.current) {
        triggerEvent("pofe_changed");
        previousPofeRef.current = currentPofe;
      }

      if (currentPofeModal !== previousPofeModalRef.current) {
        triggerEvent(currentPofeModal ? "pofe_details_modal_opened" : "pofe_details_modal_closed");
        previousPofeModalRef.current = currentPofeModal;
      }

      if (!currentPo) previousPoRef.current = null;
      if (!currentPofe) previousPofeRef.current = null;
    }
  }, [pathname, searchParams, stateChange, triggerEvent, isHotjarEnabled, capturePageView, isPostHogEnabled]);

  return null;
};

export default Tracking;
